const REDIRECT_KEY = 'postAuthRedirect';
const AUTH_PATH = '/auth';
const FALLBACK_PATH = '/';

type RedirectLocation = {
  pathname?: unknown;
  search?: unknown;
  hash?: unknown;
};

const canUseSessionStorage = (): boolean => {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
};

const buildPath = (pathname: string, search?: string, hash?: string): string => {
  const safePathname = pathname || FALLBACK_PATH;
  const safeSearch = search
    ? search.startsWith('?')
      ? search
      : `?${search.replace(/^\?/, '')}`
    : '';
  const safeHash = hash
    ? hash.startsWith('#')
      ? hash
      : `#${hash.replace(/^#/, '')}`
    : '';

  return `${safePathname}${safeSearch}${safeHash}`;
};

const normalizePath = (path: string): string => {
  const trimmed = path.trim();

  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return FALLBACK_PATH;
  }

  if (
    trimmed === AUTH_PATH ||
    trimmed.startsWith(`${AUTH_PATH}?`) ||
    trimmed.startsWith(`${AUTH_PATH}#`) ||
    trimmed.startsWith(`${AUTH_PATH}/`)
  ) {
    return FALLBACK_PATH;
  }

  return trimmed;
};

const readLocationLike = (value: unknown): RedirectLocation | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  return value as RedirectLocation;
};

const parseStatePath = (state: unknown): string | null => {
  if (!state || typeof state !== 'object') {
    return null;
  }

  const stateRecord = state as Record<string, unknown>;
  const stateSearch = typeof stateRecord.search === 'string' ? stateRecord.search : undefined;
  const stateHash = typeof stateRecord.hash === 'string' ? stateRecord.hash : undefined;

  if (typeof stateRecord.from === 'string') {
    return normalizePath(buildPath(stateRecord.from, stateSearch, stateHash));
  }

  const fromLocation = readLocationLike(stateRecord.from);
  if (!fromLocation || typeof fromLocation.pathname !== 'string') {
    return null;
  }

  const mergedSearch =
    typeof stateSearch === 'string'
      ? stateSearch
      : typeof fromLocation.search === 'string'
        ? fromLocation.search
        : undefined;
  const mergedHash =
    typeof stateHash === 'string'
      ? stateHash
      : typeof fromLocation.hash === 'string'
        ? fromLocation.hash
        : undefined;

  return normalizePath(buildPath(fromLocation.pathname, mergedSearch, mergedHash));
};

const readStoredRedirect = (): string | null => {
  if (!canUseSessionStorage()) {
    return null;
  }

  const storedRedirect = window.sessionStorage.getItem(REDIRECT_KEY);
  if (!storedRedirect) {
    return null;
  }

  return normalizePath(storedRedirect);
};

export const createAuthRedirectState = (location: { pathname: string; search?: string; hash?: string }) => {
  return {
    from: location.pathname,
    search: location.search || '',
    hash: location.hash || '',
  };
};

export const persistPostAuthRedirect = (path: string): void => {
  if (!canUseSessionStorage()) {
    return;
  }

  const normalizedPath = normalizePath(path);
  window.sessionStorage.setItem(REDIRECT_KEY, normalizedPath);
};

export const persistPostAuthRedirectFromLocation = (location: { pathname: string; search?: string; hash?: string }): void => {
  persistPostAuthRedirect(buildPath(location.pathname, location.search, location.hash));
};

export const resolvePostAuthRedirect = (state: unknown): string => {
  const statePath = parseStatePath(state);
  if (statePath) {
    persistPostAuthRedirect(statePath);
    return statePath;
  }

  return readStoredRedirect() || FALLBACK_PATH;
};

export const consumePostAuthRedirect = (fallbackPath: string = FALLBACK_PATH): string => {
  const storedRedirect = readStoredRedirect();

  if (canUseSessionStorage()) {
    window.sessionStorage.removeItem(REDIRECT_KEY);
  }

  return storedRedirect || normalizePath(fallbackPath);
};

